import { useCallback, useEffect, useState } from "react";

// Mirrors core.WebProducer (GET /v1/web/producers/{username}). Odds, pools,
// and money displays all come from the server.
export type ProducerMarketLine = {
  status: string;
  pool_display: string;
  american_odds: string;
  share_bps: number;
  backer_count: number;
};

export type ProducerContest = {
  slug: string;
  name: string;
  cover_url?: string;
  ends_at?: string;
  state: string;
  market?: ProducerMarketLine;
};

export type ProducerWin = {
  contest_name?: string;
  cover_url?: string;
  contest_slug?: string;
  placement?: number;
  amount_display: string;
  at: string;
};

export type ProducerLinks = { instagram?: string; twitter?: string; soundcloud?: string; website?: string };

export type Producer = {
  username: string;
  avatar_url?: string;
  bio?: string;
  links: ProducerLinks;
  member_since?: string;
  stats: {
    rank?: number;
    contests_entered: number;
    contests_won: number;
    total_winnings_display: string;
  };
  live: ProducerContest[];
  wins: ProducerWin[];
};

export type ProducerState =
  | { status: "loading" }
  | { status: "notfound" }
  | { status: "error" }
  | { status: "ready"; producer: Producer };

const str = (v: unknown) => (typeof v === "string" ? v : undefined);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const http = (v: unknown) => (typeof v === "string" && /^https?:\/\//.test(v) ? v : undefined);
const obj = (v: unknown) => (v && typeof v === "object" ? (v as Record<string, unknown>) : null);

// Keeps only what the page shows. A body without a username is malformed.
export function normalizeProducer(raw: unknown): Producer | null {
  const b = obj(raw);
  if (!b || typeof b.username !== "string") return null;
  const links = obj(b.links) ?? {};
  const stats = obj(b.stats) ?? {};
  const live = Array.isArray(b.live) ? b.live : [];
  const wins = Array.isArray(b.wins) ? b.wins : [];
  return {
    username: b.username,
    avatar_url: http(b.avatar_url),
    bio: str(b.bio),
    links: {
      instagram: http(links.instagram),
      twitter: http(links.twitter),
      soundcloud: http(links.soundcloud),
      website: http(links.website),
    },
    member_since: str(b.member_since),
    stats: {
      rank: typeof stats.rank === "number" ? stats.rank : undefined,
      contests_entered: num(stats.contests_entered),
      contests_won: num(stats.contests_won),
      total_winnings_display: str(stats.total_winnings_display) ?? "",
    },
    live: live
      .map(obj)
      .filter((c): c is Record<string, unknown> => !!c && typeof c.slug === "string" && typeof c.name === "string")
      .map((c) => {
        const m = obj(c.market);
        return {
          slug: c.slug as string,
          name: c.name as string,
          cover_url: http(c.cover_url),
          ends_at: str(c.ends_at),
          state: str(c.state) ?? "",
          market: m
            ? {
                status: str(m.status) ?? "",
                pool_display: str(m.pool_display) ?? "",
                american_odds: str(m.american_odds) ?? "",
                share_bps: num(m.share_bps),
                backer_count: num(m.backer_count),
              }
            : undefined,
        };
      }),
    wins: wins
      .map(obj)
      .filter((w): w is Record<string, unknown> => !!w && typeof w.amount_display === "string")
      .map((w) => ({
        contest_name: str(w.contest_name),
        cover_url: http(w.cover_url),
        contest_slug: str(w.contest_slug),
        placement: typeof w.placement === "number" ? w.placement : undefined,
        amount_display: w.amount_display as string,
        at: str(w.at) ?? "",
      })),
  };
}

export function producerURL(username: string): string {
  return `/v1/web/producers/${encodeURIComponent(username)}`;
}

export function useProducer(username: string): { state: ProducerState; retry: () => void } {
  const [state, setState] = useState<ProducerState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: "loading" });
    fetch(producerURL(username), { method: "GET", signal: ctrl.signal, headers: { Accept: "application/json" } })
      .then(async (res) => {
        if (res.status === 404) return setState({ status: "notfound" });
        if (!res.ok) throw new Error(`producer ${res.status}`);
        const producer = normalizeProducer(await res.json());
        setState(producer ? { status: "ready", producer } : { status: "error" });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ status: "error" });
      });
    return () => ctrl.abort();
  }, [username, attempt]);
  return { state, retry };
}
